
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MenuItem {
  id: string;
  title: string;
  path: string;
  icon: string;
  section: string;
  display_order: number;
  can_access: boolean;
}

type UserRole = 'admin' | 'user' | 'atc' | 'dm' | 'sales_assistant';

interface Role {
  role: UserRole;
}

export const useRolePermissions = () => {
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState<string[]>([]);

  const fetchRoles = async () => {
    try {
      const { data, error } = await supabase.rpc('get_available_roles');
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setAvailableRoles(data as Role[]);
        if (data.length > 0) {
          setSelectedRole(data[0].role as UserRole);
        }
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load available roles");
    }
  };

  const fetchMenuPermissions = async (role: UserRole) => {
    if (!role) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_menu_permissions_for_role', {
        role_name: role
      });
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setMenuItems(data);
        
        // Extract unique sections
        const uniqueSections = [...new Set(data.map(item => item.section))];
        setSections(uniqueSections);
      }
    } catch (error) {
      console.error("Error fetching menu permissions:", error);
      toast.error("Failed to load menu permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role);
  };

  const handleCheckboxChange = (menuItemId: string, checked: boolean) => {
    setMenuItems(prevItems => 
      prevItems.map(item => 
        item.id === menuItemId ? { ...item, can_access: checked } : item
      )
    );
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      // Process each menu item
      for (const item of menuItems) {
        const { error } = await supabase
          .from('role_menu_permissions')
          .upsert({
            role: selectedRole as UserRole,
            menu_item_id: item.id,
            can_access: item.can_access
          }, {
            onConflict: 'role,menu_item_id'
          });
          
        if (error) {
          throw error;
        }
      }
      
      toast.success("Role permissions updated successfully");
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchMenuPermissions(selectedRole);
    }
  }, [selectedRole]);

  return {
    availableRoles,
    selectedRole,
    menuItems,
    loading,
    saving,
    sections,
    handleRoleChange,
    handleCheckboxChange,
    savePermissions
  };
};
